import produce from 'immer';
import { comparator, filter, groupBy, pluck, sort, sum } from 'ramda';
import { AnyAction } from 'redux';
import { ActionsObservable, combineEpics, ofType, StateObservable } from 'redux-observable';
import { createSelector } from 'reselect';
import { of } from 'rxjs';
import { ajax, AjaxError, AjaxResponse } from 'rxjs/ajax';
import {
  catchError,
  delay,
  map,
  mergeMap,
  retryWhen,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import config from '../config';
import { MxAccount, MxMember, MxTransaction } from '../models/mx';

import {
  ADD_TRANSACTION,
  ADDED_TRANSACTION,
  CLEAR_ACCOUNTS_CACHE,
  CLEAR_CONNECT_URL,
  CLEARED_ACCOUNTS_CACHE,
  DELETE_TRANSACTION,
  DELETED_TRANSACTION,
  ERROR_ADD_TRANSACTION,
  ERROR_CLEAR_ACCOUNTS_CACHE,
  ERROR_DELETE_TRANSACTION,
  ERROR_FETCH_ACCOUNTS,
  ERROR_FETCH_CONNECT_URL,
  ERROR_FETCH_TRANSACTIONS,
  ERROR_HIDE_ACCOUNT,
  ERROR_IGNORE_TRANSACTION,
  ERROR_INCLUDE_TRANSACTION,
  ERROR_REMOVE_MEMBER,
  ERROR_SHOW_ACCOUNT,
  ERROR_UPDATE_TRANSACTION,
  FETCH_ACCOUNTS,
  FETCH_CONNECT_URL,
  FETCH_TRANSACTIONS,
  FOUND_ACCOUNTS,
  FOUND_CONNECT_URL,
  FOUND_TRANSACTIONS,
  HID_ACCOUNT,
  HIDE_ACCOUNT,
  IGNORE_TRANSACTION,
  IGNORED_TRANSACTION,
  INCLUDE_TRANSACTION,
  INCLUDED_TRANSACTION,
  REMOVE_MEMBER,
  REMOVED_MEMBER,
  SHOW_ACCOUNT,
  SHOWED_ACCOUNT,
  UPDATE_TRANSACTION,
  UPDATED_TRANSACTION,
} from './actions';
import { AscendState } from './index';

// todo I may also need to track the current month and year of transactions;
//  I may also need to track which month's transactions have been fetched already

// Reducer
export type MxState = {
  accounts: { data: MxAccount[]; error?: AjaxError; loading: boolean };
  connectUrl: { data: string; error?: AjaxError; loading: boolean };
  members: { data: MxMember[]; error?: AjaxError; loading: boolean };
  transactions: { data: MxTransaction[]; error?: AjaxError; loading: boolean };
};

const defaultState: MxState = {
  accounts: { data: [], loading: false },
  connectUrl: { data: '', loading: false },
  members: { data: [], loading: false },
  transactions: { data: [], loading: false },
};

export const mxReducer = (state = defaultState, action: AnyAction): MxState =>
  produce(state, (draftState) => {
    // managing loading state
    switch (action.type) {
      case FETCH_CONNECT_URL:
        draftState.connectUrl.loading = true;
        break;
      case ADD_TRANSACTION:
      case DELETE_TRANSACTION:
      case FETCH_TRANSACTIONS:
      case IGNORE_TRANSACTION:
      case INCLUDE_TRANSACTION:
      case UPDATE_TRANSACTION:
        draftState.transactions.loading = true;
        break;
      case HIDE_ACCOUNT:
      case SHOW_ACCOUNT:
        draftState.accounts.loading = true;
        break;
      case FETCH_ACCOUNTS:
      case REMOVE_MEMBER:
        draftState.accounts.loading = true;
        draftState.members.loading = true;
        break;

      case FOUND_CONNECT_URL:
        draftState.connectUrl.loading = false;
        break;
      case ADDED_TRANSACTION:
      case DELETED_TRANSACTION:
      case FOUND_TRANSACTIONS:
      case IGNORED_TRANSACTION:
      case INCLUDED_TRANSACTION:
      case UPDATED_TRANSACTION:
        draftState.transactions.loading = false;
        break;
      case HID_ACCOUNT:
      case SHOWED_ACCOUNT:
        draftState.accounts.loading = false;
        break;
      case FOUND_ACCOUNTS:
      case REMOVED_MEMBER:
        draftState.accounts.loading = false;
        draftState.members.loading = false;
        break;
    }

    // managing data state
    switch (action.type) {
      case ADDED_TRANSACTION:
        // todo if date is not in the same month, it still shows up in the table for the
        //  currently showing month, which is obviously incorrect, but a problem for another day
        draftState.transactions.data = draftState.transactions.data.concat(action.transaction);
        break;
      case CLEAR_CONNECT_URL:
        draftState.connectUrl.data = '';
        break;
      case DELETED_TRANSACTION:
        draftState.transactions.data = draftState.transactions.data.filter(
          (t) => t.guid !== action.transactionGuid
        );
        break;
      case FOUND_ACCOUNTS:
        draftState.accounts.data = action.accounts;
        draftState.members.data = action.members;
        break;
      case FOUND_CONNECT_URL:
        draftState.connectUrl.data = action.connectUrl;
        break;
      case FOUND_TRANSACTIONS:
        draftState.transactions.data = action.transactions;
        break;
      case HID_ACCOUNT:
        draftState.accounts.data = draftState.accounts.data.map((a) =>
          a.guid === action.accountGuid ? ({ ...a, is_closed: true } as MxAccount) : a
        );
        break;
      case IGNORED_TRANSACTION:
        draftState.transactions.data = draftState.transactions.data.map((t) =>
          t.guid === action.transaction.guid
            ? ({ ...t, ...action.transaction } as MxTransaction)
            : t
        );
        break;
      case INCLUDED_TRANSACTION:
        draftState.transactions.data = draftState.transactions.data.map((t) =>
          t.guid === action.transactionGuid ? ({ ...t, status: 'POSTED' } as MxTransaction) : t
        );
        break;
      case REMOVED_MEMBER:
        draftState.accounts.data = draftState.accounts.data.filter(
          (account) => account.member_guid !== action.memberGuid
        );
        draftState.members.data = draftState.members.data.filter(
          (member) => member.guid !== action.memberGuid
        );
        break;
      case SHOWED_ACCOUNT:
        draftState.accounts.data = draftState.accounts.data.map((a) =>
          a.guid === action.accountGuid ? ({ ...a, is_closed: false } as MxAccount) : a
        );
        break;
      case UPDATED_TRANSACTION:
        if (action.should_delete) {
          draftState.transactions.data = draftState.transactions.data.filter(
            (t) => t.guid !== action.transaction.guid
          );
        } else {
          draftState.transactions.data = draftState.transactions.data.map((t) =>
            t.guid === action.transaction.guid
              ? ({ ...t, ...action.transaction } as MxTransaction)
              : t
          );
        }
        break;
    }
  });

// Selectors
export function getMxState(state: AscendState) {
  return state.mx;
}

export const getMxTransactions = createSelector(getMxState, (mxState) => mxState.transactions.data);
const getNonTransferTransactions = createSelector(getMxTransactions, (transactions) =>
  filter((transaction) => transaction.top_level_category !== 'Transfer', transactions)
);
const getNonIgnoredTransactions = createSelector(getMxTransactions, (transactions) =>
  filter((transaction) => transaction.status !== 'IGNORED', transactions)
);
export const getTransactionsByCategory = createSelector(getNonIgnoredTransactions, (transactions) =>
  groupBy((transaction) => transaction.top_level_category, transactions)
);
const getIncomeTransactions = createSelector(getNonTransferTransactions, (transactions) =>
  filter((transaction) => transaction.type === 'CREDIT', transactions)
);
const getExpenseTransactions = createSelector(getNonTransferTransactions, (transactions) =>
  filter((transaction) => transaction.type === 'DEBIT', transactions)
);

const getIncludedIncomeTransactions = createSelector(getIncomeTransactions, (transactions) =>
  filter((transaction) => transaction.status !== 'IGNORED', transactions)
);
const getIncludedExpenseTransactions = createSelector(getExpenseTransactions, (transactions) =>
  filter((transaction) => transaction.status !== 'IGNORED', transactions)
);

export const getIncome = createSelector(getIncludedIncomeTransactions, (transactions) =>
  sum(pluck('amount', transactions) as number[])
);
export const getExpenses = createSelector(getIncludedExpenseTransactions, (transactions) =>
  sum(pluck('amount', transactions) as number[])
);

// sorted by date descending
const byDate = comparator((a: MxTransaction, b: MxTransaction) => a.date > b.date);
export const getSortedMxTransactions = createSelector(getMxTransactions, (transactions) =>
  sort(byDate, transactions)
);

// Epics
const addTransaction$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(ADD_TRANSACTION),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const userId = state.user.data.id;
      const url = `${config.apiUrl}/mxTransaction`;
      const { accountGuid, amount, date, description, topLevelCategory, transactionType } = action;
      const body = {
        accountGuid,
        amount,
        date,
        description,
        topLevelCategory,
        type: transactionType,
        userId,
      };
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, body, headers, method: 'POST', withCredentials: true };
      return ajax(ajaxRequest);
    }),
    map((res: AjaxResponse) => ({
      type: ADDED_TRANSACTION,
      transaction: res.response.transaction,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_ADD_TRANSACTION, error }))
  );

const deleteMxCache$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(CLEAR_ACCOUNTS_CACHE),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxCache?userId=${action.userId}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, headers, method: 'DELETE', withCredentials: true };
      return ajax(ajaxRequest).pipe(
        mergeMap(() => of({ type: CLEARED_ACCOUNTS_CACHE, userId: action.userId })),
        catchError((error: AjaxError) => of({ type: ERROR_CLEAR_ACCOUNTS_CACHE, error }))
      );
    })
  );

const deleteTransaction$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(DELETE_TRANSACTION, INCLUDE_TRANSACTION),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const userId = state.user.data.id;
      const url = `${config.apiUrl}/mxTransaction?userId=${userId}&transactionGuid=${action.transactionGuid}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, headers, method: 'DELETE', withCredentials: true };
      return ajax(ajaxRequest).pipe(
        retryWhen((errors) => errors.pipe(delay(1000), take(2))),
        mergeMap(() =>
          of({
            type: action.type === DELETE_TRANSACTION ? DELETED_TRANSACTION : INCLUDED_TRANSACTION,
            transactionGuid: action.transactionGuid,
            userId,
          })
        ),
        catchError((error: AjaxError) =>
          of({
            type:
              action.type === DELETE_TRANSACTION
                ? ERROR_DELETE_TRANSACTION
                : ERROR_INCLUDE_TRANSACTION,
            error,
          })
        )
      );
    })
  );

const getMxAccounts$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FETCH_ACCOUNTS),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxAccounts?userId=${action.userId}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest).pipe(retryWhen((errors) => errors.pipe(delay(1000), take(2))));
    }),
    map((res: AjaxResponse) => ({
      type: FOUND_ACCOUNTS,
      accounts: res.response.accounts,
      members: res.response.members,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_FETCH_ACCOUNTS, error }))
  );

const getMxConnectUrl$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FETCH_CONNECT_URL),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxConnectUrl?userId=${action.userId}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest);
    }),
    map((res: AjaxResponse) => ({
      type: FOUND_CONNECT_URL,
      connectUrl: res.response.connectUrl,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_FETCH_CONNECT_URL, error }))
  );

const getMxTransactions$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(FETCH_TRANSACTIONS),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxTransactions?userId=${action.userId}&fromDate=${action.fromDate}&toDate=${action.toDate}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = {
        url,
        headers,
        method: 'GET',
        responseType: 'json',
        withCredentials: true,
      };
      return ajax(ajaxRequest).pipe(retryWhen((errors) => errors.pipe(delay(1000), take(2))));
    }),
    map((res: AjaxResponse) => ({
      type: FOUND_TRANSACTIONS,
      transactions: res.response.transactions,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_FETCH_TRANSACTIONS, error }))
  );

const hideMxAccount$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(HIDE_ACCOUNT),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxAccount/hide`;
      const body = { accountGuid: action.accountGuid, userId: action.userId };
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, body, headers, method: 'PUT', withCredentials: true };
      return ajax(ajaxRequest).pipe(
        mergeMap(() => of({ type: HID_ACCOUNT, accountGuid: action.accountGuid })),
        catchError((error: AjaxError) => of({ type: ERROR_HIDE_ACCOUNT, error }))
      );
    })
  );

const ignoreTransaction$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(IGNORE_TRANSACTION),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const userId = state.user.data.id;
      const url = `${config.apiUrl}/mxTransaction`;
      const { accountGuid, transactionGuid } = action;
      const body = { accountGuid, transactionGuid, userId };
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, body, headers, method: 'PUT', withCredentials: true };
      return ajax(ajaxRequest);
    }),
    map((res: AjaxResponse) => ({
      type: IGNORED_TRANSACTION,
      transaction: res.response.transaction,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_IGNORE_TRANSACTION, error }))
  );

const refetchAccounts$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(CLEARED_ACCOUNTS_CACHE, REMOVED_MEMBER),
    map((action) => ({ type: FETCH_ACCOUNTS, userId: action.userId }))
  );

const removeMxMember$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(REMOVE_MEMBER),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxMember?userId=${action.userId}&memberGuid=${action.memberGuid}`;
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, headers, method: 'DELETE', withCredentials: true };
      return ajax(ajaxRequest).pipe(
        retryWhen((errors) => errors.pipe(delay(1000), take(2))),
        mergeMap(() =>
          of({
            type: REMOVED_MEMBER,
            userId: action.userId,
            memberGuid: action.memberGuid,
          })
        ),
        catchError((error: AjaxError) => of({ type: ERROR_REMOVE_MEMBER, error }))
      );
    })
  );

const showMxAccount$ = (action$: ActionsObservable<AnyAction>) =>
  action$.pipe(
    ofType(SHOW_ACCOUNT),
    switchMap((action) => {
      const url = `${config.apiUrl}/mxAccount/show`;
      const body = { accountGuid: action.accountGuid, userId: action.userId };
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, body, headers, method: 'PUT', withCredentials: true };
      return ajax(ajaxRequest).pipe(
        mergeMap(() => of({ type: SHOWED_ACCOUNT, accountGuid: action.accountGuid })),
        catchError((error: AjaxError) => of({ type: ERROR_SHOW_ACCOUNT, error }))
      );
    })
  );

const updateTransaction$ = (
  action$: ActionsObservable<AnyAction>,
  state$: StateObservable<AscendState>
) =>
  action$.pipe(
    ofType(UPDATE_TRANSACTION),
    withLatestFrom(state$),
    switchMap(([action, state]) => {
      const userId = state.user.data.id;
      const url = `${config.apiUrl}/mxTransaction`;
      const body = { ...action, type: action.transaction_type, userId };
      const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
      const ajaxRequest = { url, body, headers, method: 'PUT', withCredentials: true };
      return ajax(ajaxRequest);
    }),
    map((res: AjaxResponse) => ({
      type: UPDATED_TRANSACTION,
      transaction: res.response.transaction,
      should_delete: res.response.should_delete,
    })),
    catchError((error: AjaxError) => of({ type: ERROR_UPDATE_TRANSACTION, error }))
  );

export const mxEpic = combineEpics(
  addTransaction$,
  deleteMxCache$,
  deleteTransaction$,
  getMxAccounts$,
  getMxConnectUrl$,
  getMxTransactions$,
  hideMxAccount$,
  ignoreTransaction$,
  refetchAccounts$,
  removeMxMember$,
  showMxAccount$,
  updateTransaction$
);
